const WAITLIST_UNSUBSCRIBE_PATH = '/waitlist/unsubscribe';

/** Matches /waitlist/unsubscribe with optional trailing slash. */
export function isWaitlistUnsubscribePathname(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  return normalized === WAITLIST_UNSUBSCRIBE_PATH;
}

/** Avoid catastrophic backtracking on long hosts/paths. */
const EMBEDDED_UNSUBSCRIBE_TOKEN =
  /((?:https?:\/\/[^\s"'<>/]+)?\/waitlist\/unsubscribe\/?(?:\?[^"'#\s]*)?)(?:&|^|\?)token=[^"'&\s#]*/gi;

export const EMBEDDED_UNSUBSCRIBE_SCRUB_MAX_LENGTH = 8192;

function stripTokenFragmentsFromOverflowSuffix(suffix: string): string {
  return suffix
    .replace(/\/waitlist\/unsubscribe\/?(?:\?[^"'#\s]*)?/gi, (match) =>
      /token=/i.test(match) ? '/waitlist/unsubscribe' : match,
    )
    .replace(/(?:^|[?&])token=[^&\s"'#]*/gi, '')
    .replace(/(?:^|[?&])t%6[fF]ken(?:=|%3[dD])[^&\s"'#]*/gi, '')
    .replace(/(?:^|[?&])%74oken=[^&\s"'#]*/gi, '')
    .replace(/token=[^&\s"'#]*/gi, '');
}

function replaceEmbeddedUnsubscribeTokens(text: string): string {
  if (text.length <= EMBEDDED_UNSUBSCRIBE_SCRUB_MAX_LENGTH) {
    return text.replace(EMBEDDED_UNSUBSCRIBE_TOKEN, '$1');
  }
  const prefix = text.slice(0, EMBEDDED_UNSUBSCRIBE_SCRUB_MAX_LENGTH);
  const suffix = stripTokenFragmentsFromOverflowSuffix(
    text.slice(EMBEDDED_UNSUBSCRIBE_SCRUB_MAX_LENGTH),
  );
  return prefix.replace(EMBEDDED_UNSUBSCRIBE_TOKEN, '$1') + suffix;
}

function finalizeUnsubscribeUrlStrip(original: string, working: string): string {
  if (original.length <= EMBEDDED_UNSUBSCRIBE_SCRUB_MAX_LENGTH) {
    return working;
  }
  const prefix = working.slice(0, EMBEDDED_UNSUBSCRIBE_SCRUB_MAX_LENGTH);
  const suffix = stripTokenFragmentsFromOverflowSuffix(
    working.slice(EMBEDDED_UNSUBSCRIBE_SCRUB_MAX_LENGTH),
  );
  return prefix + suffix;
}

function deleteTokenSearchParams(params: URLSearchParams): void {
  for (const key of Array.from(params.keys())) {
    let decoded = key;
    try {
      decoded = decodeURIComponent(key);
    } catch {
      /* keep key */
    }
    const normalized = decoded.toLowerCase();
    if (
      normalized === 'token' ||
      key === 'token' ||
      normalized === 't%6fken' ||
      /token/i.test(decoded)
    ) {
      params.delete(key);
    }
  }
  for (const [key, val] of Array.from(params.entries())) {
    if (typeof val === 'string' && /token/i.test(val)) {
      params.delete(key);
    }
  }
}

function stripEncodedTokenQuerySegments(url: string): string {
  return url
    .replace(/([?&])(?:token|t%6[fF]ken|%74oken)(?:=|%3[dD])[^&#'"\s]*/gi, '$1')
    .replace(/%3[fF](?:token|t%6[fF]ken|%74oken)%3[dD][^&#'"\s]*/gi, '?')
    .replace(/%26(?:token|t%6[fF]ken|%74oken)%3[dD][^&#'"\s]*/gi, '&')
    .replace(/\?&/g, '?')
    .replace(/[?&]$/g, '');
}

/** Removes `token` from waitlist unsubscribe URLs (and embedded URLs in free text). */
export function stripWaitlistUnsubscribeTokenFromUrl(url: string): string {
  let working = url;
  try {
    const fullyDecoded = decodeURIComponent(url);
    if (
      fullyDecoded.includes('waitlist/unsubscribe') &&
      fullyDecoded !== url
    ) {
      working = fullyDecoded;
    }
  } catch {
    working = url;
  }
  working = stripEncodedTokenQuerySegments(working);
  working = replaceEmbeddedUnsubscribeTokens(working);
  try {
    const decoded = decodeURIComponent(working);
    if (decoded !== working && decoded.includes('waitlist/unsubscribe')) {
      working = stripEncodedTokenQuerySegments(decoded);
    }
  } catch {
    /* keep working */
  }
  try {
    const base = working.startsWith('http') ? undefined : 'http://localhost';
    const parsed = new URL(working, base);
    if (!isWaitlistUnsubscribePathname(parsed.pathname)) {
      return finalizeUnsubscribeUrlStrip(url, working);
    }
    deleteTokenSearchParams(parsed.searchParams);
    let search = parsed.searchParams.toString();
    if (search.includes('%74oken') || /(?:^|&)token=/.test(`?${search}`)) {
      search = search
        .replace(/(?:^|&)(?:%74oken|token)=[^&]*/gi, '')
        .replace(/^&/, '');
    }
    const path = parsed.pathname.replace(/\/+$/, '') || WAITLIST_UNSUBSCRIBE_PATH;
    const pathAndQuery = search ? `${path}?${search}` : path;
    const hash = parsed.hash;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return finalizeUnsubscribeUrlStrip(
        url,
        `${parsed.origin}${pathAndQuery}${hash}`,
      );
    }
    if (working.startsWith('/')) {
      return finalizeUnsubscribeUrlStrip(url, `${pathAndQuery}${hash}`);
    }
    return finalizeUnsubscribeUrlStrip(url, `${pathAndQuery}${hash}`);
  } catch {
    return finalizeUnsubscribeUrlStrip(
      url,
      working
        .replace(/([?&])(?:token|%74oken)=[^&]*/gi, '$1')
        .replace(/\?&/, '?')
        .replace(/[?&]$/, ''),
    );
  }
}

export function stripTokenFromFreeText(text: string): string {
  return stripWaitlistUnsubscribeTokenFromUrl(text);
}

export function analyticsSafeHref(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return stripWaitlistUnsubscribeTokenFromUrl(window.location.href);
}

export function analyticsSafePathname(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    return new URL(analyticsSafeHref()).pathname;
  } catch {
    return window.location.pathname;
  }
}

const POSTHOG_URL_KEYS = new Set([
  '$current_url',
  '$referrer',
  '$initial_referrer',
  '$initial_current_url',
  '$pathname',
  '$search',
  '$pageleave',
  '$prev_pageview',
  '$screen_name',
]);

function sanitizePosthogValue(key: string, value: unknown): unknown {
  if (typeof value === 'string') {
    if (POSTHOG_URL_KEYS.has(key) || key.includes('url') || key.includes('URL')) {
      return stripWaitlistUnsubscribeTokenFromUrl(value);
    }
    if (value.includes('waitlist/unsubscribe') && value.includes('token=')) {
      return stripTokenFromFreeText(value);
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item, index) =>
      sanitizePosthogValue(`${key}[${index}]`, item),
    );
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [nestedKey, nested] of Object.entries(value as Record<string, unknown>)) {
      out[nestedKey] = sanitizePosthogValue(nestedKey, nested);
    }
    return out;
  }
  return value;
}

/** Strip unsubscribe tokens from PostHog event properties (and nested autocapture payloads). */
export function sanitizePosthogProperties(
  properties: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!properties) return properties;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    out[key] = sanitizePosthogValue(key, value);
  }
  return out;
}

export function sanitizePosthogEvent<T extends { properties?: Record<string, unknown> }>(
  event: T,
): T {
  if (event.properties) {
    event.properties = sanitizePosthogProperties(event.properties) ?? {};
  }
  return event;
}

export function isUnsubscribeRoutePathname(pathname?: string): boolean {
  if (!pathname) return false;
  return isWaitlistUnsubscribePathname(pathname);
}
